<template>
  <transition name="alert" appear>
    <div v-if="visible" :class="alertClasses">
      <div class="bert-alert-icon" v-if="showIcon">
        <slot name="icon">
          <svg v-if="variant === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20,6 9,17 4,12"></polyline>
          </svg>
          <svg v-else-if="variant === 'warning'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <triangle points="7.86,2 16.14,2 22,13.86 18.14,22 5.86,22 0,13.86"></triangle>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          <svg v-else-if="variant === 'error'" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
        </slot>
      </div>
      
      <div class="bert-alert-content">
        <div v-if="title" class="bert-alert-title">{{ title }}</div>
        <div class="bert-alert-message">
          <slot>{{ message }}</slot>
        </div>
      </div>
      
      <button v-if="closable" @click="close" class="bert-alert-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'BertAlert',
  props: {
    visible: {
      type: Boolean,
      default: true
    },
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'success', 'warning', 'error', 'neutral'].includes(value)
    },
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: ''
    },
    closable: {
      type: Boolean,
      default: false
    },
    showIcon: {
      type: Boolean,
      default: true
    },
    outline: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    alertClasses() {
      return [
        'bert-alert',
        `bert-alert--${this.variant}`,
        {
          'bert-alert--outline': this.outline
        }
      ]
    }
  },
  methods: {
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.bert-alert {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-4);
  border-radius: var(--radius-md);
  border: 1px solid transparent;
}

/* Variants - Filled */
.bert-alert--primary {
  background-color: var(--primary-50);
  color: var(--primary-800);
  border-color: var(--primary-200);
}

.bert-alert--success {
  background-color: var(--success-50);
  color: var(--success-800);
  border-color: var(--success-200);
}

.bert-alert--warning {
  background-color: var(--warning-50);
  color: var(--warning-800);
  border-color: var(--warning-200);
}

.bert-alert--error {
  background-color: var(--error-50);
  color: var(--error-800);
  border-color: var(--error-200);
}

.bert-alert--neutral {
  background-color: var(--neutral-50);
  color: var(--neutral-800);
  border-color: var(--neutral-200);
}

/* Variants - Outline */
.bert-alert--primary.bert-alert--outline {
  background-color: white;
  border-color: var(--primary-300);
}

.bert-alert--success.bert-alert--outline {
  background-color: white;
  border-color: var(--success-300);
}

.bert-alert--warning.bert-alert--outline {
  background-color: white;
  border-color: var(--warning-300);
}

.bert-alert--error.bert-alert--outline {
  background-color: white;
  border-color: var(--error-300);
}

.bert-alert--neutral.bert-alert--outline {
  background-color: white;
  border-color: var(--neutral-300);
}

.bert-alert-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-top: 2px;
}

.bert-alert-content {
  flex: 1;
}

.bert-alert-title {
  font-weight: var(--font-semibold);
  font-size: var(--text-sm);
  margin-bottom: var(--space-1);
}

.bert-alert-message {
  font-size: var(--text-sm);
  line-height: 1.5;
}

.bert-alert-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--space-1);
  border-radius: var(--radius-sm);
  color: currentColor;
  opacity: 0.7;
  transition: all var(--transition-base);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.bert-alert-close:hover {
  opacity: 1;
  background-color: rgba(0, 0, 0, 0.1);
}

/* Transitions */
.alert-enter-active,
.alert-leave-active {
  transition: all var(--transition-base);
}

.alert-enter-from,
.alert-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>